import { fetchWorkspaces as fetchWorkspacesFor, type Workspace } from '@inboundx/shared/platform';

import { supabase } from '@/lib/supabase';

export {
    isPathWorkspace,
    type Workspace,
    type WorkspaceStatus,
    workspaceLocation,
} from '@inboundx/shared/platform';

/** Always the selected workspace's host, so existing links and bookmarks keep working. */
export const WORKSPACE_DOMAIN_URL_PARAM = 'domain';
/** Set only for a workspace placed under a path, where the host alone is ambiguous. */
export const WORKSPACE_ID_URL_PARAM = 'workspace';

/**
 * Query params that open the backoffice on a workspace, for links read outside the app
 * (Linear tickets). `workspaceId` is optional: the host alone selects the workspace that owns it.
 */
export function workspaceUrlParams(domain: string, workspaceId?: string): URLSearchParams {
    const params = new URLSearchParams({ [WORKSPACE_DOMAIN_URL_PARAM]: domain });
    if (workspaceId) params.set(WORKSPACE_ID_URL_PARAM, workspaceId);
    return params;
}

/** How a ticket names a workspace for a human: `Sunday FR (https://www.sundayapp.com/fr)`. */
export function workspaceLabel(workspace: Workspace): string {
    const path = workspace.pathPrefix === '/' ? '' : workspace.pathPrefix;
    return `${workspace.name} (https://${workspace.host}${path})`;
}

/** The workspaces the signed-in backoffice user can select. */
export function fetchWorkspaces(): Promise<Workspace[]> {
    return fetchWorkspacesFor(supabase);
}

/**
 * Other workspaces rooted under `workspace` on the same host (apex and `www` alike).
 * Their pages are synced there, never here: knowledge ingestion leaves them out.
 */
export function nestedWorkspaces(workspace: Workspace, workspaces: Workspace[]): Workspace[] {
    const bareHost = (host: string) => host.replace(/^www\./, '');
    const under = workspace.pathPrefix === '/' ? '/' : `${workspace.pathPrefix}/`;
    return workspaces.filter(
        (other) =>
            other.id !== workspace.id &&
            bareHost(other.host) === bareHost(workspace.host) &&
            other.pathPrefix.length > workspace.pathPrefix.length &&
            other.pathPrefix.startsWith(under)
    );
}
